/**
 * Flow-builder text blocks
 *
 * The prompts the flow builder writes are assembled from these blocks. Every tenant sees the
 * system set and can override any block for itself ("Hausstil"); a platform admin can change
 * the system block that everyone without an override uses. `{{@name}}` are builder variables
 * (resolved while assembling), `{{name}}` are runtime placeholders the message writer fills.
 */

import { Alert, Box, Chip, CircularProgress, Collapse, IconButton, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getStoreBlocks, resetStoreBlock, updateStoreBlock } from 'API/global-funnels';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

export interface StoreBlock {
  role: string;
  label: string;
  language: 'de' | 'en';
  text: string;
  source: 'system' | 'tenant';
  systemText: string | null;
  tenantText: string | null;
}

const StoreBlockEditor: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const [blocks, setBlocks] = useState<StoreBlock[]>([]);
  const [canEditSystem, setCanEditSystem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openRole, setOpenRole] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getStoreBlocks(language);
      setBlocks(res.data?.blocks ?? []);
      setCanEditSystem(res.data?.canEditSystem === true);
      setDraft({});
    } catch {
      setError(t('storeBlocksLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [language, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceBlock = (updated: StoreBlock) => {
    setBlocks((prev) => prev.map((b) => (b.role === updated.role ? updated : b)));
    setDraft((prev) => {
      const next = { ...prev };
      delete next[updated.role];
      return next;
    });
  };

  const save = async (block: StoreBlock, scope: 'tenant' | 'system') => {
    const text = (draft[block.role] ?? block.text).trim();
    if (!text) return;
    setSaving(block.role);
    setNotice(null);
    try {
      const res = await updateStoreBlock(block.role, { language, text, scope });
      replaceBlock(res.data as StoreBlock);
      setNotice(t('storeBlocksSaved'));
    } catch (e) {
      const message = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(message ? t('storeBlocksRejected', { message }) : t('storeBlocksSaveFailed'));
    } finally {
      setSaving(null);
    }
  };

  const reset = async (block: StoreBlock) => {
    setSaving(block.role);
    setNotice(null);
    try {
      const res = await resetStoreBlock(block.role, language);
      replaceBlock(res.data as StoreBlock);
      setNotice(t('storeBlocksResetDone'));
    } catch {
      setError(t('storeBlocksResetFailed'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Stack gap={2} className={classes.root}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2} flexWrap={'wrap'}>
        <Box>
          <Typography className={classes.title}>{t('storeBlocksTitle')}</Typography>
          <Typography className={classes.intro}>{t('storeBlocksIntro')}</Typography>
        </Box>
        <PaiSelect
          value={language}
          variant={'background'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLanguage(e.target.value as 'de' | 'en')}
          style={{ minWidth: 160 }}
        >
          <PaiMenuItem value={'de'}>{t('flowBuilderLangDe')}</PaiMenuItem>
          <PaiMenuItem value={'en'}>{t('flowBuilderLangEn')}</PaiMenuItem>
        </PaiSelect>
      </Stack>
      <Typography className={classes.hint}>{t('storeBlocksVariablesHint')}</Typography>

      {error && <Alert severity={'error'} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity={'success'} onClose={() => setNotice(null)}>{notice}</Alert>}

      {loading ? (
        <Stack alignItems={'center'} py={4}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <Stack gap={1}>
          {blocks.map((block) => {
            const open = openRole === block.role;
            const value = draft[block.role] ?? block.text;
            const dirty = value !== block.text;
            const found = [...new Set([...value.matchAll(/\{\{\s*(@?[a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g)].map((m) => m[1]))];
            return (
              <Box key={block.role} className={cx(classes.block, block.source === 'tenant' && classes.blockTenant)}>
                <Stack direction={'row'} alignItems={'center'} gap={1} className={classes.blockHeader} onClick={() => setOpenRole(open ? null : block.role)}>
                  <Typography className={classes.blockLabel}>{block.label}</Typography>
                  <Typography className={classes.blockRole}>{block.role}</Typography>
                  <Chip
                    size={'small'}
                    label={block.source === 'tenant' ? t('storeBlocksSourceTenant') : t('storeBlocksSourceSystem')}
                    color={block.source === 'tenant' ? 'primary' : 'default'}
                    variant={'outlined'}
                  />
                  <IconButton size={'small'} className={cx(classes.expand, open && 'open')} aria-label={'toggle'}>
                    <ExpandMoreIcon />
                  </IconButton>
                </Stack>
                <Collapse in={open} unmountOnExit>
                  <Stack gap={1.5} className={classes.blockBody}>
                    <TextField
                      multiline
                      minRows={3}
                      maxRows={16}
                      fullWidth
                      value={value}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [block.role]: e.target.value }))}
                      inputProps={{ maxLength: 8000 }}
                    />
                    {found.length > 0 && (
                      <Stack direction={'row'} flexWrap={'wrap'} gap={0.5} alignItems={'center'}>
                        <Typography className={classes.systemLabel}>{t('storeBlocksPlaceholdersFound')}</Typography>
                        {found.map((name) => (
                          <Chip key={name} size={'small'} label={`{{${name}}}`} variant={'outlined'} color={name.startsWith('@') ? 'default' : 'primary'} />
                        ))}
                      </Stack>
                    )}
                    {block.source === 'tenant' && block.systemText && (
                      <Box className={classes.systemText}>
                        <Typography className={classes.systemLabel}>{t('storeBlocksSystemVersion')}</Typography>
                        <Typography className={classes.systemBody}>{block.systemText}</Typography>
                      </Box>
                    )}
                    <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                      <PaiButton text={t('storeBlocksSave')} size={'small'} border={'rounded'} disabled={!dirty || !value.trim()} loading={saving === block.role} action={() => void save(block, 'tenant')} />
                      {canEditSystem && (
                        <PaiButton text={t('storeBlocksSaveSystem')} size={'small'} border={'rounded'} disabled={!dirty || !value.trim()} loading={saving === block.role} action={() => void save(block, 'system')} />
                      )}
                      {block.source === 'tenant' && <PaiButton text={t('storeBlocksReset')} size={'small'} border={'rounded'} loading={saving === block.role} action={() => void reset(block)} />}
                    </Stack>
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  title: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
  },
  intro: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
  hint: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  block: {
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
  },
  blockTenant: {
    borderColor: theme.palette.primary.main,
  },
  blockHeader: {
    padding: theme.spacing(1, 1.5),
    cursor: 'pointer',
  },
  blockLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    flexGrow: 1,
  },
  blockRole: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  expand: {
    transition: '225ms transform',
    '&.open': {
      transform: 'rotate(180deg)',
    },
  },
  blockBody: {
    padding: theme.spacing(0, 1.5, 1.5, 1.5),
  },
  systemText: {
    padding: theme.spacing(1),
    borderRadius: 6,
    backgroundColor: theme.palette.greys.lightest,
  },
  systemLabel: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  systemBody: {
    fontSize: theme.typography.pxToRem(12),
    whiteSpace: 'pre-wrap',
  },
}));

export default StoreBlockEditor;
